import {
	AppButton,
	Checkbox,
	Dropdown,
	PageHeader,
	StatCard
} from '@clickguard/design-system'
import Joi from 'joi'
import { useEffect, useMemo, useState } from 'react'
import { HiOutlineMail } from 'react-icons/hi'
import { FiBell } from 'react-icons/fi'
import { useUiStore } from 'store'
import { useStores } from '../../../hooks'

type notificationPrefType =
	| 'newsletter'
	| 'service'
	| 'links'
	| 'subscription'
	| 'team'
	| 'protection'
	| 'reports'

const notificationPreferences = {
	all: 'All notifications',
	newsletter: 'News and promotions',
	service: 'Service interruptions and alerts',
	links: 'Changes to PPC platform connections',
	subscription: 'Subscription and payment events',
	team: 'Additions and changes to team members',
	protection: 'Changes to protection settings',
	reports: 'Delivery of automated and on-demand reports'
}

const emailSchema = Joi.string()
	.email({ tlds: { allow: false } })
	.min(3)
	.max(30)
	.required()

export const NotificationSetting = () => {
	const { userStore } = useStores()
	const { updateNotifications, user, updateEmail } = userStore
	const isLoading = useUiStore((s) => s.isLoading)

	const [errors, setErrors] = useState<null | string>()

	const preference = useMemo(() => {
		const preference = user?.preferences?.notifications
		if (preference?.length === 7) preference.push('all')
		return preference
	}, [user])
	const email_preference = useMemo(() => {
		const preference = user?.preferences?.email_notifications
		if (preference?.length === 7) preference.push('all')
		return preference
	}, [user])
	const [emailValue, setEmailValue] = useState<string>(
		user?.contact_email || ''
	)
	const [verifiedEmails, setVerifiedEmails] = useState<
		{ value: string; label: string }[]
	>([])

	const [notifications, setNotifications] = useState<string[]>(preference || [])
	const [emailNotifications, setEmailNotifications] = useState<string[]>(
		email_preference || []
	)
	const validate = (value: string) => {
		const { error } = emailSchema.validate(value || emailValue)
		if (!error) return null
		return error.message
	}
	const handleEmailChange = (value: string) => {
		setEmailValue(value)
		setErrors(validate(value))
	}

	const handleChange = (
		e: boolean,
		name:
			| 'all'
			| 'newsletter'
			| 'service'
			| 'links'
			| 'subscription'
			| 'team'
			| 'protection'
			| 'reports'
	) => {
		if (name === 'all') {
			if (e) {
				// Set all notifications when 'all' is selected
				setNotifications([
					'all',
					'newsletter',
					'service',
					'links',
					'subscription',
					'team',
					'protection',
					'reports'
				])
			} else {
				setNotifications([])
			}
		} else {
			const form = notifications.filter((value) => value)
			if (!e) {
				const newForm = form.filter((value) => value !== name)
				setNotifications(newForm)
				if (newForm.length === 0 || newForm.includes('all')) {
					setNotifications(newForm.filter((value) => value !== 'all'))
				}
			} else {
				form.push(name)
				if (form.length === 7) {
					setNotifications([...form, 'all'])
				} else if (form.length === 8) {
					setNotifications(form.filter((value) => value !== 'all'))
				} else {
					setNotifications(form)
				}
			}
		}
	}
	const handleEmailNotificationChange = (
		e: boolean,
		name:
			| 'all'
			| 'newsletter'
			| 'service'
			| 'links'
			| 'subscription'
			| 'team'
			| 'protection'
			| 'reports'
	) => {
		if (name === 'all') {
			if (e) {
				// Set all notifications when 'all' is selected
				setEmailNotifications([
					'all',
					'newsletter',
					'service',
					'links',
					'subscription',
					'team',
					'protection',
					'reports'
				])
			} else {
				setEmailNotifications([])
			}
		} else {
			const form = emailNotifications.filter((value) => value)
			if (!e) {
				const newForm = form.filter((value) => value !== name)
				setEmailNotifications(newForm)
				if (newForm.length === 0 || newForm.includes('all')) {
					setEmailNotifications(newForm.filter((value) => value !== 'all'))
				}
			} else {
				form.push(name)
				if (form.length === 7) {
					setEmailNotifications([...form, 'all'])
				} else if (form.length === 8) {
					setEmailNotifications(form.filter((value) => value !== 'all'))
				} else {
					setEmailNotifications(form)
				}
			}
		}
	}

	const handleConfirm = async () => {
		const app_notification_values = notifications.filter(
			(value) => value !== 'all'
		)
		const email_notification_values = emailNotifications.filter(
			(value) => value !== 'all'
		)
		if (emailValue !== user?.contact_email && emailValue) {
			await updateEmail({ email: emailValue })
		}
		if (
			app_notification_values.sort().join(',') !==
				user?.preferences?.notifications.sort().join(',') ||
			email_notification_values.sort().join(',') !==
				user?.preferences?.notifications.sort().join(',')
		) {
			await updateNotifications({
				app_notification_values,
				email_notification_values
			})
		}
	}

	useEffect(() => {
		const configEmailsArray: { value: string; label: string }[] = []
		const verifiedEmail = user?.email_addresses.filter(
			(email) => email.email_verified
		)
		verifiedEmail?.forEach((item) =>
			configEmailsArray.push({ label: item.email, value: item.email })
		)
		setVerifiedEmails([...configEmailsArray])
	}, [user?.email_addresses])

	return (
		<div className="w-full">
			<PageHeader
				layout="stacked"
				title="Notifications"
				subtitle="Manage your notification preferences"
			/>
			<div className="pt-4">
				<StatCard
					size="regular"
					divider={false}
					className="w-full !p-4 !gap-4"
					title={
						<span className="text-[18px] leading-6 font-medium text-t-base-blue">
							Notifications
						</span>
					}
					trailing={
						<AppButton
							loader="dots"
							variant="primary"
							size="md"
							label="Save"
							onClick={() => handleConfirm()}
							isLoading={isLoading('USER_SAVE_NOTIFICATIONS')}
						/>
					}
				>
					<div className="flex w-[calc(50%-8px)] flex-col gap-1.5">
						<p className="text-[14px] leading-[18px] font-medium text-[color:var(--text-primary)]">
							Email address
						</p>
						<Dropdown
							className="w-full [&>button]:w-full [&>button]:!justify-between [&>button]:!font-normal"
							menuWidth="trigger"
							placeholder="Select email address"
							value={emailValue || undefined}
							options={verifiedEmails}
							onValueChange={handleEmailChange}
						/>
						{errors && (
							<p className="text-[12px] leading-4 text-[color:var(--error-ink,#f04438)]">{errors}</p>
						)}
					</div>

					<div className="flex flex-col">
						<div className="flex items-center gap-2 border-b border-t-traffic pb-3">
							<p className="flex-1 text-[14px] leading-[18px] font-medium text-[color:var(--text-secondary)]">
								Notification
							</p>
							<div className="flex w-24 items-center justify-center gap-1.5 text-[14px] font-medium text-[color:var(--text-secondary)]">
								<FiBell className="h-4 w-4" />
								App
							</div>
							<div className="flex w-24 items-center justify-center gap-1.5 text-[14px] font-medium text-[color:var(--text-secondary)]">
								<HiOutlineMail className="h-4 w-4" />
								Email
							</div>
						</div>
						{Object.keys(notificationPreferences).map((key) => (
							<div
								key={key}
								className="flex items-center gap-2 py-3"
							>
								<p
									className={`flex-1 text-[14px] leading-[18px] ${
										key === 'all'
											? 'font-medium text-[color:var(--text-primary)]'
											: 'font-normal text-[color:var(--text-secondary)]'
									}`}
								>
									{notificationPreferences[key as notificationPrefType]}
								</p>
								<div className="flex w-24 justify-center">
									<Checkbox
										name={key}
										checked={notifications.includes(key)}
										onChange={(v) =>
											handleChange(
												v.target.checked,
												key as notificationPrefType
											)
										}
									/>
								</div>
								<div className="flex w-24 justify-center">
									<Checkbox
										name={key}
										checked={emailNotifications.includes(key)}
										onChange={(v) =>
											handleEmailNotificationChange(
												v.target.checked,
												key as notificationPrefType
											)
										}
									/>
								</div>
							</div>
						))}
					</div>
				</StatCard>
			</div>
		</div>
	)
}
